'use client';
import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { ContentCards as ContentCardsType } from '@lib/types/cms/ContentCards';
import { isDefined } from '@lib/util/isDefined';
import { toNonNullable } from '@lib/util/toNonNullable';
import c from 'classnames';
import { ButtonStyle, ContentCard, ContentCardProps } from 'ui-components';
import { BlocksTitle } from '../shared/BlocksTitle';

type ContentCardsProps = {
  data: ContentCardsType;
  position: number;
  isDarkTheme?: boolean;
};

const contentCardsGridCols = {
  3: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
};

const ContentCards = ({
  data,
  position,
  isDarkTheme = false,
}: ContentCardsProps) => {
  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: data.titleInternal || '',
      typename: 'ContentCards',
      position,
      trackAsPromo: data.trackAsPromo ?? false,
      ctas: data.cards.map((card) => card.cta?.label).filter(isDefined),
    });

  if (!isDefined(data) || data.cards.length === 0) {
    return null;
  }

  const renderableContentCards: ContentCardProps[] = data.cards
    .map((card, index) => {
      return {
        title: toNonNullable(card.header),
        id: `content-card-${card.id}-${index}`,
        description: card.description!,
        image:
          card.image && card.image.url
            ? {
                url: card.image.url,
                alt: toNonNullable(card.image.alt),
              }
            : undefined,
        cta:
          card.cta && card.cta.label && card.cta.url
            ? {
                label: card.cta.label,
                url: card.cta.url,
                openInNewTab: card.cta.openInNewTab || false,
              }
            : undefined,
        ctaStyle: data.ctaAppearance as ButtonStyle,
        label: toNonNullable(card.label),
        onCtaClick: () => promoClickTrackingEvent(card.cta?.label || ''),
      };
    })
    .filter(isDefined);

  const cardCount = renderableContentCards.length;

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={data.trackAsPromo ?? false}
    >
      <div className="container">
        {(data.title || data.description || data.text) && (
          <div className="mb-3">
            <BlocksTitle
              title={data.title}
              description={data.description}
              text={data.text}
              isDarkTheme={isDarkTheme}
            />
          </div>
        )}
        <ul
          className={c(
            'grid grid-flow-dense gap-4 xl:gap-5',
            contentCardsGridCols[cardCount]
          )}
          data-test="content-cards-wrapper"
        >
          {renderableContentCards.map((card) => (
            <li key={card.id} data-test="content-card">
              <ContentCard {...card} isDarkTheme={isDarkTheme} />
            </li>
          ))}
        </ul>
      </div>
    </TrackPromotionImpression>
  );
};

export default ContentCards;
